import { ScheduleGrid } from "@/components/molecules/schedule-grid";
import { Badge } from "@/components/ui/badge";
import type { Schedule } from "@/features/calendar/types";
import { getNextException } from "@/lib/rrule-utils";
import { CaretCircleDoubleUpIcon } from "@phosphor-icons/react";
import type { ColumnDef } from "@tanstack/react-table";
import { useDateFormat, useTranslation } from "@/features/utils/i18n";
import { Action } from "./columns-action";

export function useSchedulesColumns() {
  const { t } = useTranslation("schedules");
  const { format } = useDateFormat();

  const columns: ColumnDef<Schedule>[] = [
    {
      accessorKey: "name",
      header: t("table.name"),
      cell: ({ row }) => (
        <div className="flex items-center gap-2">
          <div className="font-medium">{row.original.name}</div>
          {row.original.is_default && (
            <Badge variant="secondary" className="text-xs">
              <CaretCircleDoubleUpIcon className="h-3 w-3 mr-1" weight="fill" />
              {t("table.default")}
            </Badge>
          )}
        </div>
      ),
    },
    {
      id: "schedule",
      header: t("table.openingHours"),
      cell: ({ row }) => <ScheduleGrid rrules={row.original.rrules} />,
    },
    {
      accessorKey: "timezone",
      header: t("table.timezone"),
      cell: ({ row }) => (
        <Badge variant="outline" className="text-xs">
          {row.original.timezone}
        </Badge>
      ),
    },
    {
      id: "exceptions",
      header: t("table.nextException"),
      cell: ({ row }) => {
        const nextException = getNextException(row.original.rrules);

        if (!nextException) {
          return (
            <span className="text-xs text-muted-foreground">
              {t("table.noExceptions")}
            </span>
          );
        }

        const date = new Date(nextException.nextDate + "T00:00:00");
        const isClosed = nextException.slots.length === 0;

        return (
          <div className="flex flex-col gap-0.5">
            <div className="text-xs font-medium text-red-600">
              {nextException.name}
            </div>
            <div className="text-xs text-muted-foreground">
              {format(date, "PP")}
              {" - "}
              {isClosed
                ? t("table.closed")
                : t("table.openSlots", { count: nextException.slots.length })}
            </div>
          </div>
        );
      },
    },
    {
      id: "actions",
      header: () => <div className="text-right">{t("table.actions")}</div>,
      cell: ({ row }) => <Action schedule={row.original} />,
    },
  ];

  return columns;
}
